// PhotoThumb (job page round): a photo's small copy, a button that opens it full size (PhotoViewer).
// 88 × 66, or 44 × 33 in a closed line, or a square tile (PhotoTiles' line, round 3: no "office" word on
// it; who sees a photo is said in the viewer), or as wide as its cell at 4:3 (PhotoTiles' grid, the job's
// photos and files). A video draws "▶ Video" and opens like a photo; a file draws its words and opens in
// a new tab. Its frame is a hairline all round, or a rule under it only (frame below: the job's photos
// and files, never boxed). It draws what a person needs before they open it: "office" on a
// photo only the office sees, a pen when the tech drew on it, a dashed frame with "Uploading…" while its
// file is on its way, and, when the file won't load, a crossed frame that says so rather than an empty
// box. Chosen (the one the viewer shows) is the heavier line (DESIGN.md §2). The photo is cropped to fill
// the frame, so a tall one shows its middle.
// Reads only --thumb-*.
import { useState } from 'react';
import { Button as AriaButton } from 'react-aria-components';
import { Glyph } from '../Glyph/Glyph.tsx';
import './PhotoThumb.css';

/** A photo as the office sees it: the words are ready to show. */
export interface Photo {
  id: string;
  /** The photo as taken. */
  src: string;
  /** The same photo with the tech's markup drawn on it, when there is one. */
  markupSrc?: string;
  /** Its note; also its name for screen readers ("Leak at the base of the old tank"). */
  caption: string;
  /** Who took it and when, in words ("Sam Rivera · Wed Oct 21, 9:52 a.m."). */
  by?: string;
  /** Where it belongs, in words ("Visit 1, before"). */
  where?: string;
  /** When it was taken, in words, under a tile ("Sun Oct 25, 9:24 a.m."). */
  taken?: string;
  /** Only the office sees it: it isn't on the invoice or in the customer's portal. */
  officeOnly?: boolean;
  /** A photo (the default), a video (its words on the tile; it opens like a photo) or a file to open. */
  kind?: 'photo' | 'video' | 'file';
  /** A file's link, opened in a new tab; none, its words alone. */
  href?: string;
  /** A video's or a file's words on its tile ("Open the PDF"); a video's default is "▶ Video". */
  face?: string;
  /**
   * Its file is still on its way ("Uploading…", "Checking…"): the thumbnail says so in a dashed frame,
   * can't be opened, and the viewer leaves it out until it lands.
   */
  waiting?: string;
}

export interface PhotoThumbProps {
  photo: Photo;
  /** md 88 × 66 (a visit's photos), sm 44 × 33 (a closed line's strip), tile: a square; fill: as wide
   * as its cell, 4:3. */
  size?: 'sm' | 'md' | 'tile' | 'fill';
  /** all: a hairline all round (the default); below: a rule under it only. Waiting is dashed all round. */
  frame?: 'all' | 'below';
  /** Its name for screen readers in place of photoName's ("Open the old tank full size"). */
  label?: string;
  /** The one the viewer shows now. */
  isSelected?: boolean;
  /** Draws the copy with the markup on it, when there is one (the default). */
  showMarkup?: boolean;
  onPress?: (id: string) => void;
}

/** What a screen reader says for a photo: its note, then what the thumbnail draws. */
export function photoName(p: Photo): string {
  return [p.caption, p.officeOnly ? 'office only' : '', p.markupSrc ? 'marked up' : '', p.waiting ?? '']
    .filter(Boolean)
    .join(', ');
}

export function PhotoThumb(props: PhotoThumbProps) {
  const { photo, size = 'md', frame, label } = props;
  if (photo.waiting)
    return (
      <span
        className="fl-thumb"
        data-size={size}
        data-frame={frame}
        data-waiting=""
        role="img"
        aria-label={label ?? photoName(photo)}
      >
        {size !== 'sm' ? (
          <span className="fl-thumb__wait" aria-hidden="true">
            {photo.waiting}
          </span>
        ) : null}
      </span>
    );
  if (photo.kind === 'file') {
    const words = <span className="fl-thumb__face">{photo.face}</span>;
    return photo.href ? (
      <a
        className="fl-thumb"
        data-size={size}
        data-frame={frame}
        data-face="file"
        href={photo.href}
        target="_blank"
        rel="noreferrer"
        {...(label ? { 'aria-label': label } : {})}
      >
        {words}
      </a>
    ) : (
      <span className="fl-thumb" data-size={size} data-frame={frame} data-face="file">
        {words}
      </span>
    );
  }
  if (photo.kind === 'video')
    return (
      <AriaButton
        className="fl-thumb"
        data-size={size}
        data-frame={frame}
        data-face="video"
        aria-label={label ?? photoName(photo)}
        onPress={() => props.onPress?.(photo.id)}
      >
        <span className="fl-thumb__face">{photo.face ?? '▶ Video'}</span>
      </AriaButton>
    );
  return <Thumb {...props} />;
}

function Thumb({
  photo,
  size = 'md',
  frame,
  label,
  isSelected,
  showMarkup = true,
  onPress,
}: PhotoThumbProps) {
  const src = showMarkup && photo.markupSrc ? photo.markupSrc : photo.src;
  // a failed load is remembered for this file only: a new src tries again
  const [failedSrc, setFailedSrc] = useState<string | null>(null);
  const failed = failedSrc === src;
  const full = size !== 'sm';
  return (
    <AriaButton
      className="fl-thumb"
      data-size={size}
      data-frame={frame}
      data-selected={isSelected ? '' : undefined}
      aria-label={`${label ?? photoName(photo)}${failed ? ', didn’t load' : ''}`}
      aria-current={isSelected ? 'true' : undefined}
      onPress={() => onPress?.(photo.id)}
    >
      {failed ? (
        <span className="fl-thumb__failed">
          <Glyph name="image-off" className="fl-thumb__glyph" />
          {full ? <span className="fl-thumb__failed-words">Didn’t load</span> : null}
        </span>
      ) : (
        <img
          className="fl-thumb__img"
          src={src}
          alt=""
          title={[photo.caption, photo.by].filter(Boolean).join(' · ')}
          loading="lazy"
          decoding="async"
          draggable={false}
          onError={() => setFailedSrc(src)}
        />
      )}
      {size === 'md' && photo.officeOnly ? (
        <span className="fl-thumb__tag" aria-hidden="true">
          office
        </span>
      ) : null}
      {full && photo.markupSrc ? (
        <span className="fl-thumb__mark" aria-hidden="true">
          <Glyph name="pencil" className="fl-thumb__mark-glyph" />
        </span>
      ) : null}
    </AriaButton>
  );
}

export interface MoreThumbProps {
  /** How many aren't shown. */
  count: number;
  size?: 'sm' | 'md';
  onPress?: () => void;
}

/** The last tile of a capped row: "+6", which opens the viewer at the first one not shown. */
export function MoreThumb({ count, size = 'md', onPress }: MoreThumbProps) {
  return (
    <AriaButton
      className="fl-thumb"
      data-size={size}
      data-more=""
      aria-label={`${count} more photo${count === 1 ? '' : 's'}`}
      onPress={onPress}
    >
      <span className="fl-thumb__more" aria-hidden="true">
        +{count}
      </span>
    </AriaButton>
  );
}
